import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Trophy, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getMedalStyle } from '@/lib/medals';
import { useAuth } from '@/context/AuthContext';
import { RACE_LEADERBOARD } from '@/graphql/queries';

interface LeaderboardPreviewProps {
    raceId: string;
}

export function LeaderboardPreview({ raceId }: LeaderboardPreviewProps) {
    const { t } = useTranslation('races');
    const { user: currentUser } = useAuth();

    const { data, loading } = useQuery(RACE_LEADERBOARD, {
        variables: { raceId, pagination: { page: 1, limit: 3 } },
    });

    if (loading) return null;

    const items = data?.raceLeaderboard?.items ?? [];
    if (items.length === 0) return null;

    return (
        <div className="rounded-2xl bg-amber-50/50 p-4 border border-amber-100">
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
                <Trophy size={18} className="text-amber-500" />
                <h3 className="text-sm font-bold text-amber-800">{t('details.leaderboardPreview.title')}</h3>
            </div>

            {/* Top 3 */}
            <div className="space-y-2">
                {items.map((item, index) => {
                    const rank = index + 1;
                    const isMe = item.user?.id === currentUser?.id;
                    return (
                        <div
                            key={item.user?.id ?? index}
                            className={cn(
                                "flex items-center gap-3 py-2 px-3 rounded-xl",
                                isMe && "bg-amber-100/50"
                            )}
                        >
                            <div className={cn(
                                "w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0",
                                getMedalStyle(rank)
                            )}>
                                {rank}
                            </div>
                            <span className={cn(
                                "flex-1 text-sm font-medium truncate",
                                isMe ? "text-amber-900 font-bold" : "text-slate-700"
                            )}>
                                {item.user?.username ?? '?'}
                                {isMe && (
                                    <span className="ml-1.5 text-[10px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full">
                                        {t('details.leaderboardPreview.you')}
                                    </span>
                                )}
                            </span>
                            <span className="text-sm font-black text-slate-700 flex-shrink-0">
                                {item.totalScore?.toFixed(1)}
                            </span>
                            <span className="text-[10px] text-slate-400 font-medium flex-shrink-0">
                                {t('details.leaderboardPreview.pts')}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* Link to full leaderboard */}
            <Link
                to={`/races/${raceId}/leaderboard`}
                className="mt-3 flex items-center justify-center gap-1 text-sm font-bold text-amber-700 hover:text-amber-800 transition-colors pt-3 border-t border-amber-100"
            >
                {t('details.leaderboardPreview.viewFull')}
                <ChevronRight size={16} />
            </Link>
        </div>
    );
}
